import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Upload,
  Copy,
  CheckCircle2,
  Building2,
  Link2,
  Users,
  Sparkles,
  PartyPopper,
  Share2,
  Mail,
  MessageSquare,
  Shield,
  ExternalLink,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const ONBOARDING_STEPS = [
  {
    id: "welcome",
    icon: Sparkles,
    titleKey: "Welcome to OmbudNow!",
    titleKeyPt: "Bem-vindo ao OmbudNow!",
  },
  {
    id: "logo",
    icon: Building2,
    titleKey: "Personalize your channel",
    titleKeyPt: "Personalize seu canal",
  },
  {
    id: "share",
    icon: Share2,
    titleKey: "Share with your team",
    titleKeyPt: "Compartilhe com sua equipe",
  },
  {
    id: "complete",
    icon: PartyPopper,
    titleKey: "You're all set!",
    titleKeyPt: "Tudo pronto!",
  },
];

export default function OnboardingWizard({ 
  empresa, 
  slug, 
  user, 
  onComplete, 
  onLogoUpload,
  isOpen,
  onClose 
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'pt';
  const [currentStep, setCurrentStep] = useState(0);
  const [copiedLink, setCopiedLink] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(empresa?.logo_url || null);
  const [shareEmails, setShareEmails] = useState("");

  const denunciaLink = `${window.location.origin}/${slug}/denuncia`;
  const ouvidoriaLink = `${window.location.origin}/${slug}/ouvidoria`;

  const content = {
    pt: {
      steps: {
        welcome: {
          title: "Bem-vindo ao OmbudNow!",
          subtitle: `Olá, ${user?.nome?.split(" ")[0]}! Vamos configurar o canal de compliance da ${empresa?.nome || "sua empresa"} em poucos passos.`,
          features: [
            "Canal de denúncias anônimo e seguro",
            "Ouvidoria para sugestões e reclamações",
            "Dashboard completo para gestão de casos",
            "Relatórios e métricas em tempo real"
          ],
          cta: "Vamos começar!"
        },
        logo: {
          title: "Personalize seu canal",
          subtitle: "Adicione o logo da sua empresa para que os colaboradores reconheçam o canal oficial.",
          currentLogo: "Logo atual",
          uploadNew: "Carregar novo logo",
          uploading: "Enviando...",
          tip: "Dica: Use uma imagem PNG ou JPG com fundo transparente para melhor resultado.",
          skip: "Pular por agora",
          continue: "Seguir"
        },
        share: {
          title: "Compartilhe com sua equipe",
          subtitle: "Envie os links dos canais para seus colaboradores começarem a usar.",
          whistleblowing: "Canal de Denúncias (Anônimo)",
          ombudsman: "Canal de Ouvidoria (Identificado)",
          copyLink: "Copiar link",
          copied: "Copiado!",
          shareVia: "Compartilhar via",
          emailPlaceholder: "emails@empresa.com (separados por vírgula)",
          sendInvites: "Enviar convites",
          orCopy: "Ou copie os links acima e compartilhe manualmente"
        },
        complete: {
          title: "Tudo pronto!",
          subtitle: "Seu canal de compliance está configurado e pronto para receber denúncias e manifestações.",
          nextSteps: "Próximos passos:",
          tips: [
            "Divulgue os links em comunicados internos",
            "Adicione os links na intranet da empresa",
            "Inclua informações no onboarding de novos colaboradores",
            "Configure alertas de email para novos casos"
          ],
          cta: "Ir para o Dashboard"
        }
      },
      navigation: {
        next: "Próximo",
        previous: "Anterior",
        skip: "Pular",
        finish: "Concluir"
      }
    },
    en: {
      steps: {
        welcome: {
          title: "Welcome to OmbudNow!",
          subtitle: `Hi, ${user?.nome?.split(" ")[0]}! Let's set up the compliance channel for ${empresa?.nome || "your company"} in a few steps.`,
          features: [
            "Anonymous and secure whistleblowing channel",
            "Ombudsman for suggestions and complaints",
            "Complete dashboard for case management",
            "Real-time reports and metrics"
          ],
          cta: "Let's get started!"
        },
        logo: {
          title: "Personalize your channel",
          subtitle: "Add your company logo so employees recognize the official channel.",
          currentLogo: "Current logo",
          uploadNew: "Upload new logo",
          uploading: "Uploading...",
          tip: "Tip: Use a PNG or JPG image with transparent background for best results.",
          skip: "Skip for now",
          continue: "Continue"
        },
        share: {
          title: "Share with your team",
          subtitle: "Send the channel links to your employees to start using.",
          whistleblowing: "Whistleblowing Channel (Anonymous)",
          ombudsman: "Ombudsman Channel (Identified)",
          copyLink: "Copy link",
          copied: "Copied!",
          shareVia: "Share via",
          emailPlaceholder: "emails@company.com (comma separated)",
          sendInvites: "Send invites",
          orCopy: "Or copy the links above and share manually"
        },
        complete: {
          title: "You're all set!",
          subtitle: "Your compliance channel is configured and ready to receive reports and requests.",
          nextSteps: "Next steps:",
          tips: [
            "Share the links in internal communications",
            "Add the links to your company intranet",
            "Include information in new employee onboarding",
            "Configure email alerts for new cases"
          ],
          cta: "Go to Dashboard"
        }
      },
      navigation: {
        next: "Next",
        previous: "Previous",
        skip: "Skip",
        finish: "Finish"
      }
    }
  };

  const c = content[currentLang];

  const copyToClipboard = (text, linkType) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(linkType);
    toast.success(currentLang === 'en' ? "Link copied!" : "Link copiado!");
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploadingLogo(true);
    try {
      if (onLogoUpload) {
        await onLogoUpload(file);
      }
      toast.success(currentLang === 'en' ? "Logo updated!" : "Logo atualizado!");
    } catch (error) {
      toast.error(currentLang === 'en' ? "Error uploading logo" : "Erro ao enviar logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleShareEmail = () => {
    if (!shareEmails.trim()) {
      toast.error(currentLang === 'en' ? "Please enter email addresses" : "Por favor, insira os emails");
      return;
    }
    
    const subject = encodeURIComponent(
      currentLang === 'en' 
        ? `${empresa?.nome} - Compliance Channel` 
        : `${empresa?.nome} - Canal de Compliance`
    );
    const body = encodeURIComponent(
      currentLang === 'en'
        ? `Hello,\n\nWe now have a compliance channel for reports and requests.\n\nWhistleblowing (Anonymous): ${denunciaLink}\nOmbudsman (Identified): ${ouvidoriaLink}\n\nAll information is confidential.\n\nBest regards`
        : `Olá,\n\nAgora temos um canal de compliance para denúncias e manifestações.\n\nCanal de Denúncias (Anônimo): ${denunciaLink}\nOuvidoria (Identificado): ${ouvidoriaLink}\n\nTodas as informações são confidenciais.\n\nAtenciosamente`
    );
    
    window.open(`mailto:${shareEmails}?subject=${subject}&body=${body}`, '_blank');
    toast.success(currentLang === 'en' ? "Opening email client..." : "Abrindo cliente de email...");
  };

  const handleComplete = () => {
    // Trigger confetti celebration
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
    
    // Mark onboarding as complete
    const onboardingKey = `ombudnow_onboarding_${slug}_${user?.email}`;
    localStorage.setItem(onboardingKey, "completed");
    
    // Close wizard
    setTimeout(() => {
      if (onComplete) onComplete();
      if (onClose) onClose();
    }, 1500);
  };

  const nextStep = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  const StepIcon = ONBOARDING_STEPS[currentStep].icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => {
          // Mark onboarding as dismissed when clicking backdrop
          const onboardingKey = `ombudnow_onboarding_${slug}_${user?.email}`;
          localStorage.setItem(onboardingKey, "dismissed");
          if (onClose) onClose();
        }}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Close button */}
        <button
          onClick={() => {
            // Mark onboarding as dismissed when closing via X button
            const onboardingKey = `ombudnow_onboarding_${slug}_${user?.email}`;
            localStorage.setItem(onboardingKey, "dismissed");
            if (onClose) onClose();
          }}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-slate-100 transition-colors"
          data-testid="close-onboarding"
        >
          <X className="w-5 h-5 text-slate-500" />
        </button>

        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div 
            className="h-full bg-gradient-to-r from-[#1a1a2e] to-[#f5a623] transition-all duration-500"
            style={{ width: `${((currentStep + 1) / ONBOARDING_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Step indicator */}
        <div className="flex justify-center gap-2 pt-6">
          {ONBOARDING_STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === currentStep 
                  ? "bg-[#1a1a2e] scale-125" 
                  : index < currentStep 
                    ? "bg-[#f5a623]" 
                    : "bg-slate-200"
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Step 1: Welcome */}
          {currentStep === 0 && (
            <div className="text-center" data-testid="onboarding-step-welcome">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1a1a2e] to-[#2d2d4a] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Sparkles className="w-10 h-10 text-[#f5a623]" />
              </div>
              <h2 className="text-2xl font-bold text-[#1a1a2e] mb-3">
                {c.steps.welcome.title}
              </h2>
              <p className="text-slate-600 mb-8 max-w-md mx-auto">
                {c.steps.welcome.subtitle}
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {c.steps.welcome.features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl text-left"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>

              <Button 
                onClick={nextStep}
                className="bg-[#1a1a2e] hover:bg-[#2d2d4a] px-8"
                data-testid="btn-start-onboarding"
              >
                {c.steps.welcome.cta}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Logo */}
          {currentStep === 1 && (
            <div className="text-center" data-testid="onboarding-step-logo">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1a1a2e] to-[#2d2d4a] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Building2 className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#1a1a2e] mb-3">
                {c.steps.logo.title}
              </h2>
              <p className="text-slate-600 mb-8">
                {c.steps.logo.subtitle}
              </p>

              <div className="bg-slate-50 rounded-2xl p-6 mb-6">
                <p className="text-sm text-slate-500 mb-4">{c.steps.logo.currentLogo}</p>
                <div className="w-32 h-32 mx-auto bg-white rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img 
                      src={logoPreview.startsWith('http') ? logoPreview : logoPreview.startsWith('data:') ? logoPreview : `${process.env.REACT_APP_BACKEND_URL}${logoPreview}`}
                      alt="Logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <Building2 className="w-12 h-12 text-slate-300" />
                  )}
                </div>
              </div>

              <input
                type="file"
                id="logo-upload-wizard"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <label htmlFor="logo-upload-wizard">
                <Button 
                  asChild
                  className="bg-[#1a1a2e] hover:bg-[#2d2d4a] cursor-pointer"
                  disabled={uploadingLogo}
                >
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingLogo ? c.steps.logo.uploading : c.steps.logo.uploadNew}
                  </span>
                </Button>
              </label>

              <p className="text-xs text-slate-400 mt-4">{c.steps.logo.tip}</p>

              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  {c.navigation.previous}
                </Button>
                <Button onClick={nextStep} variant={logoPreview ? "default" : "outline"} className={logoPreview ? "bg-[#1a1a2e] hover:bg-[#2d2d4a]" : ""}>
                  {logoPreview ? c.steps.logo.continue : c.steps.logo.skip}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Share */}
          {currentStep === 2 && (
            <div data-testid="onboarding-step-share">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-[#f5a623] to-[#e09612] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Share2 className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-[#1a1a2e] mb-3">
                  {c.steps.share.title}
                </h2>
                <p className="text-slate-600">
                  {c.steps.share.subtitle}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                {/* Whistleblowing Link */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-5 h-5 text-[#1a1a2e]" />
                    <span className="font-medium text-[#1a1a2e]">{c.steps.share.whistleblowing}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={denunciaLink} 
                      readOnly 
                      className="bg-white text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(denunciaLink, "denuncia")}
                      data-testid="copy-denuncia-wizard"
                    >
                      {copiedLink === "denuncia" ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Ombudsman Link */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <MessageSquare className="w-5 h-5 text-[#f5a623]" />
                    <span className="font-medium text-[#1a1a2e]">{c.steps.share.ombudsman}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={ouvidoriaLink} 
                      readOnly 
                      className="bg-white text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(ouvidoriaLink, "ouvidoria")}
                      data-testid="copy-ouvidoria-wizard"
                    >
                      {copiedLink === "ouvidoria" ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  {c.navigation.previous}
                </Button>
                <Button onClick={nextStep} className="bg-[#1a1a2e] hover:bg-[#2d2d4a]">
                  {c.navigation.next}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {currentStep === 3 && (
            <div className="text-center" data-testid="onboarding-step-complete">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <PartyPopper className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#1a1a2e] mb-3">
                {c.steps.complete.title}
              </h2>
              <p className="text-slate-600 mb-8">
                {c.steps.complete.subtitle}
              </p>

              <div className="bg-slate-50 rounded-2xl p-6 mb-8 text-left">
                <p className="font-medium text-[#1a1a2e] mb-4">{c.steps.complete.nextSteps}</p>
                <ul className="space-y-3">
                  {c.steps.complete.tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#1a1a2e] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-xs font-bold">{index + 1}</span>
                      </div>
                      <span className="text-slate-600 text-sm">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex justify-between">
                <Button variant="ghost" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  {c.navigation.previous}
                </Button>
                <Button 
                  onClick={handleComplete}
                  className="bg-gradient-to-r from-[#1a1a2e] to-[#f5a623] hover:opacity-90 px-8"
                  data-testid="btn-complete-onboarding"
                >
                  {c.steps.complete.cta}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
